import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./side-nav.component.html?ngResource";
import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChildren } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SidebarModule } from 'primeng/sidebar';
import { MenuModule } from 'primeng/menu';
import { TreeModule } from 'primeng/tree';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { buildMainMenuNodes, buildAdminMenuNode, buildAddNewDashboardNode, } from './side-nav.constants';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import debounce from 'lodash/debounce';
import { throttle } from 'lodash';
import set from 'lodash/set';
import logo from 'Images/epic-rv-logo.svg';
import UserInfoService from '../../../components/base/infoServices/UserInfoService';
import DashboardInfoService from '../../../components/base/infoServices/DashboardInfoService';
import GroupService from '../../../components/admin/groups/GroupService';
import ResponsiveBreakpointsConfig from '../../../components/base/configs/ResponsiveBreakpointsConfig';
const WINDOW_RESIZE_THROTTLE_MS = 100;
let SideNavComponent = class SideNavComponent {
    userInfoService;
    dashboardInfoService;
    groupService;
    cdr;
    treeNodes;
    treeNodesSubscription;
    /**
     * Fast lookup maps for each top-level section, keyed by `${label}::${key}`
     */
    nodeLookupMaps = {};
    /**
     * Helper to set children on a node while preserving the original list for filtering/restoration.
     *
     * @param {TreeNode} node - The node to update
     * @param {TreeNode[]} children - The full list of children to assign
     */
    setNodeChildren(node, children) {
        if (children.length > 0) {
            node.children = children;
            node.leaf = false;
        }
        else {
            node.children = undefined;
            node.leaf = true;
        }
        set(node, 'data.originalChildren', [...children]);
    }
    /**
     * Recursively restore all nodes to their original children and clear highlighting.
     *
     * @param {TreeNode[]} nodes - The list of nodes to restore
     */
    restoreAllChildren(nodes) {
        for (const node of nodes) {
            if (node.data?.labelParts) {
                delete node.data.labelParts;
            }
            const original = node.data?.originalChildren;
            if (original) {
                if (original.length > 0) {
                    node.children = [...original];
                    node.leaf = false;
                    node.expanded = false;
                    this.restoreAllChildren(node.children);
                }
                else {
                    node.children = undefined;
                    node.leaf = true;
                }
            }
        }
    }
    /**
     * Build lookup maps for all top-level sections (Sites, Dashboards, Reports, Admin)
     */
    buildAllNodeLookupMaps() {
        if (!Array.isArray(this.nodes)) {
            return;
        }
        for (const node of this.nodes) {
            if (this.topLevelKeys.has(String(node.label))) {
                this.nodeLookupMaps[String(node.label)] = this.buildNodeLookupMap(node.children || []);
            }
        }
    }
    /**
     * Recursively build a lookup map for a section's subtree
     *
     * @param {TreeNode[]} nodes - Array of tree nodes to index
     * @returns {SectionMap} Map of composite key to TreeNode
     */
    buildNodeLookupMap(nodes) {
        const map = new Map();
        const traverse = (node) => {
            const key = this.getNodeLookupKey(node);
            map.set(key, node);
            if (Array.isArray(node.children)) {
                node.children.forEach(traverse);
            }
        };
        nodes.forEach(traverse);
        return map;
    }
    sidebarVisible = false;
    collapsed = false;
    sidenavWidth = '265px';
    filterText = '';
    nodes = [];
    filteredNodes = [];
    selectedNode = null;
    /** Keep track of the last node the user explicitly clicked to preserve context across navigations */
    lastClickedNode = null;
    shouldScrollToSelected = false;
    // Cached AngularJS $state to avoid repeated injector lookups on each navigation
    angularJsState;
    /** Enforce only one top-level open at a time */
    topLevelKeys = new Set(['Sites', 'Dashboards', 'Reports', 'Admin']);
    // Replace with actual environment service when available
    environment = { appName: 'Platform' };
    logoSrc = logo;
    isDev = false;
    // Event listener cleanup
    eventCleanupFns = [];
    // Cache isMobile result to avoid repeated calculations
    isMobileCache = false;
    constructor(userInfoService, dashboardInfoService, groupService, cdr) {
        this.userInfoService = userInfoService;
        this.dashboardInfoService = dashboardInfoService;
        this.groupService = groupService;
        this.cdr = cdr;
        // Initialize width from localStorage if present
        const savedWidth = localStorage.getItem('sidenav-width');
        if (savedWidth && (/^\d+px$/).test(savedWidth)) {
            this.sidenavWidth = savedWidth;
        }
        // Initialize with empty nodes, will be populated in ngOnInit
        this.nodes = [];
        this.filteredNodes = [];
        // Initialize mobile cache
        this.isMobileCache = this.calculateIsMobile();
    }
    trackByNodeKey(_index, node) {
        return node.key ?? node;
    }
    filterDebounceMs = 250;
    debouncedFilterNodes = null;
    onFilterChange() {
        if (!this.debouncedFilterNodes) {
            this.debouncedFilterNodes = debounce(() => {
                this.refreshFilteredNodes();
                if (this.selectedNode) {
                    this.shouldScrollToSelected = true;
                    this.expandAllParents(this.selectedNode);
                }
                this.cdr.detectChanges();
            }, this.filterDebounceMs);
        }
        this.debouncedFilterNodes();
    }
    clearSearch() {
        this.filterText = '';
        this.restoreAllChildren(this.nodes);
        this.filteredNodes = [...this.nodes];
        if (this.selectedNode) {
            this.expandAllParents(this.selectedNode);
            this.shouldScrollToSelected = true;
        }
        this.cdr.detectChanges();
    }
    getNodeLookupKey(node) {
        return `${node.label ?? ''}::${node.key ?? ''}`;
    }
    /**
     * Splice a label into parts based on search matches for UI highlighting.
     *
     * @param {string} label - The label to split
     * @param {string} search - The search term to match against
     * @returns {LabelPartFilterMatch[]} - The parts of the label, with matched segments marked for highlighting
     */
    computeLabelParts(label, search) {
        if (!search) {
            return [{ text: label, isMatch: false }];
        }
        const escaped = search.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&');
        const regex = new RegExp(escaped, 'gi');
        let lastIndex = 0;
        const parts = [];
        let match;
        while ((match = regex.exec(label)) !== null) {
            if (match.index > lastIndex) {
                parts.push({ text: label.slice(lastIndex, match.index), isMatch: false });
            }
            parts.push({ text: match[0], isMatch: true });
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < label.length) {
            parts.push({ text: label.slice(lastIndex), isMatch: false });
        }
        return parts;
    }
    /**
     * Perform recursive tree filtering while pre-calculating label highlights.
     *
     * @param {TreeNode[]} nodes - The nodes to filter
     * @param {string} search - The raw search term
     * @param {boolean} isTopLevel - Whether these are top-level menu nodes that should always be shown
     * @returns {TreeNode[]} - The filtered nodes
     */
    performInPlaceFilter(nodes, search, isTopLevel = false) {
        return nodes.filter((node) => {
            const label = node.label || '';
            const parts = this.computeLabelParts(label, search);
            // If all parts are non-matches, then this node doesn't match the search
            const isMatch = !(parts.length === 1 && !parts[0].isMatch);
            // Update node data in-place to preserve object reference integrity
            if (isMatch) {
                node.data = { ...node.data, labelParts: parts };
            }
            else if (node.data?.labelParts) {
                delete node.data.labelParts;
            }
            const original = node.data?.originalChildren || node.children || [];
            this.setNodeChildren(node, original); // Reset to original children before re-filtering
            // Case 1: Children match (priority)
            const matchingChildren = original.length
                ? this.performInPlaceFilter(original, search)
                : [];
            if (matchingChildren.length > 0) {
                node.children = matchingChildren;
                node.expanded = true;
                return true;
            }
            // Case 2: Node matches (show all children)
            if (isMatch) {
                if (original.length > 0) {
                    node.children = [...original];
                    node.leaf = false;
                    this.restoreAllChildren(node.children);
                    node.expanded = true;
                }
                else {
                    node.children = undefined;
                    node.leaf = true;
                }
                return true;
            }
            // Case 3: Top-level sections always stay visible
            if (isTopLevel) {
                if (original.length > 0) {
                    node.children = [...original];
                    node.leaf = false;
                    this.restoreAllChildren(node.children);
                }
                else {
                    node.children = undefined;
                    node.leaf = true;
                }
                node.expanded = false;
                return true;
            }
            return false;
        });
    }
    ngOnInit() {
        // Build the initial tree structure
        this.nodes = this.buildTree();
        this.filteredNodes = [...this.nodes];
        // Initialize originalChildren for static nodes
        this.nodes.forEach((node) => {
            this.setNodeChildren(node, node.children || []);
        });
        this.buildAllNodeLookupMaps();
        // Prefer grouped dashboards if user setting enabled or when on profile page
        if (this.userInfoService.hasGroupedBoardsViewEnabled()) {
            this.loadGroupedDashboards();
        }
        else {
            this.loadDashboards();
        }
        this.highlightCurrentRoute();
        this.setupLocationChangeListener();
        this.setupMobileSidebarListener();
        // Initialize CSS variable that controls main content padding
        this.updateSidenavWidthVar();
        // Keep CSS var in sync on viewport changes
        const resizeHandler = throttle(() => {
            this.updateSidenavWidthVar();
            this.updateIsMobileCache(); // Update mobile cache on resize
        }, WINDOW_RESIZE_THROTTLE_MS, { leading: false, trailing: true });
        window.addEventListener('resize', resizeHandler);
        this.eventCleanupFns.push(() => {
            resizeHandler.cancel();
            window.removeEventListener('resize', resizeHandler);
        });
        // Cache AngularJS injector state
        const ng = window.angular;
        const injector = ng?.element?.(document.body)?.injector?.();
        const $state = (injector?.get &&
            injector.get('$state'));
        if ($state?.go) {
            this.angularJsState = $state;
        }
        // Listen for dashboard refresh events from AngularJS
        const dashboardRefreshHandler = () => {
            if (this.userInfoService.hasGroupedBoardsViewEnabled()) {
                this.loadGroupedDashboards();
            }
            else {
                this.loadDashboards();
            }
        };
        window.addEventListener('dashboard-refresh', dashboardRefreshHandler);
        this.eventCleanupFns.push(() => window.removeEventListener('dashboard-refresh', dashboardRefreshHandler));
    }
    ngAfterViewInit() {
        // Subscribe to tree node query changes to re-apply highlighting when nodes are filtered/loaded
        if (this.treeNodes) {
            this.treeNodesSubscription = this.treeNodes.changes.subscribe(() => {
                this.scrollSelectedIntoView();
            });
        }
        // Attempt initial scroll in case data was already loaded or pre-populated
        this.scrollSelectedIntoView();
    }
    ngOnDestroy() {
        if (this.treeNodesSubscription) {
            this.treeNodesSubscription.unsubscribe();
        }
        // Clean up all event listeners to prevent memory leaks
        this.eventCleanupFns.forEach(cleanup => cleanup());
        this.eventCleanupFns = [];
        // Clean up resize handlers
        window.removeEventListener('mousemove', this.onResizing);
        window.removeEventListener('mouseup', this.onResizeEnd);
        // Cancel lodash debounce if active
        if (this.debouncedFilterNodes) {
            this.debouncedFilterNodes.cancel();
            this.debouncedFilterNodes = null;
        }
    }
    // UI helpers
    isMobile() {
        // Return cached value instead of calculating every time
        return this.isMobileCache;
    }
    calculateIsMobile() {
        return window.innerWidth <= ResponsiveBreakpointsConfig.smallTablet.maxSize;
    }
    updateIsMobileCache() {
        const newValue = this.calculateIsMobile();
        if (this.isMobileCache !== newValue) {
            this.isMobileCache = newValue;
            this.cdr.detectChanges();
        }
    }
    toggleSidebar() {
        this.sidebarVisible = !this.sidebarVisible;
        this.cdr.detectChanges();
    }
    toggleCollapse() {
        // Desktop collapse/expand
        this.collapsed = !this.collapsed;
        this.updateSidenavWidthVar();
        this.cdr.detectChanges();
    }
    onCloseMenu() {
        this.sidebarVisible = false;
    }
    onNodeSelect(event) {
        const node = event.node;
        if (!node) {
            return;
        }
        if (node.type === 'url') {
            const ctrlKeyPressed = event.originalEvent?.ctrlKey || event.originalEvent?.metaKey;
            if (ctrlKeyPressed) {
                // Open in new tab if Ctrl/Cmd key is pressed
                return;
            }
            // Prevent default link behavior to allow AngularJS or pushState navigation
            event.originalEvent?.preventDefault();
            event.originalEvent?.stopPropagation();
        }
        // Handle parent nodes with children
        if (this.isParentNode(node)) {
            this.handleParentNodeSelection(node);
        }
        else if (node.data) {
            this.selectedNode = node;
            this.lastClickedNode = node;
            this.shouldScrollToSelected = true;
            // Ensure all parents are expanded to show selection
            this.expandAllParents(node);
            this.navigateToNode(node);
            if (this.isMobileCache) {
                this.onCloseMenu();
            }
        }
        this.cdr.detectChanges();
    }
    isParentNode(node) {
        return Array.isArray(node.children) && node.children.length > 0;
    }
    handleParentNodeSelection(node) {
        const wasExpanded = node.expanded;
        if (this.topLevelKeys.has(String(node.label)) && !wasExpanded) {
            this.collapseAllParentNodes();
        }
        node.expanded = !wasExpanded;
    }
    navigateToNode(node) {
        const data = node.data || {};
        // Prefer AngularJS ui-router for SPA navigation
        if (data.state && this.navigateWithAngularJS(data.state, data.params)) {
            return;
        }
        // Fallback to pushState to avoid full reload
        if (data.url) {
            window.history.pushState({}, '', data.url);
            window.dispatchEvent(new Event('locationchange'));
        }
    }
    navigateWithAngularJS(state, params) {
        if (this.angularJsState?.go) {
            this.angularJsState.go(state, params || {});
            return true;
        }
        return false;
    }
    onNodeExpand(event) {
        const node = event.node;
        if (!node) {
            return;
        }
        if (this.topLevelKeys.has(String(node.label))) {
            // Collapse all other parent nodes when expanding one
            this.collapseAllParentNodes();
        }
        // Expand the current node
        node.expanded = true;
        this.cdr.detectChanges();
    }
    onNodeCollapse(event) {
        const node = event.node;
        if (node) {
            node.expanded = false;
            this.cdr.detectChanges();
        }
    }
    expandAllParents(node) {
        let parent = node.parent;
        while (parent) {
            parent.expanded = true;
            parent = parent.parent;
        }
    }
    buildTree() {
        // Determine user role for admin menu filtering
        const userRole = this.userInfoService.getCurrentUserKind();
        const isAdmin = this.userInfoService.isCurrentUserAdmin();
        // Build main menu structure using constants
        const nodes = buildMainMenuNodes();
        // Add dashboards children
        const dashboardNode = nodes.find(node => node.label === 'Dashboards');
        if (dashboardNode && this.userInfoService.isCurrentUserSuperAdmin()) {
            const children = [buildAddNewDashboardNode()];
            this.setNodeChildren(dashboardNode, children);
        }
        // Add admin section if user has admin privileges
        if (isAdmin) {
            const adminNode = buildAdminMenuNode(userRole);
            nodes.push(adminNode);
        }
        return nodes;
    }
    async loadDashboards() {
        try {
            const boards = await this.dashboardInfoService.getAllDashboards();
            const dashboardNode = this.nodes.find(node => node.label === 'Dashboards');
            if (!dashboardNode) {
                return;
            }
            // Sort dashboards alphabetically by name
            const sortedBoards = boards.sort((a, b) => a.name.localeCompare(b.name));
            const dynamicChildren = sortedBoards.map(b => this.createDashboardNode(b));
            // Add "Add New" item for super admins
            const children = this.userInfoService.isCurrentUserSuperAdmin()
                ? [buildAddNewDashboardNode(), ...dynamicChildren]
                : dynamicChildren;
            this.setNodeChildren(dashboardNode, children);
            // Re-run highlight after dashboards load
            this.highlightCurrentRoute();
            // Ensure filtered view reflects latest nodes
            this.refreshFilteredNodes();
        }
        catch {
            // ignore
        }
        finally {
            this.cdr.detectChanges();
        }
    }
    async loadGroupedDashboards() {
        try {
            const treeResp = await this.groupService.getGroupTree(true);
            const boards = await this.dashboardInfoService.getAllDashboards();
            const dashboardsById = new Map((boards || []).map(board => [board.id, board.name]));
            // Find Dashboards node
            const dashboardNode = this.nodes.find(node => node.label === 'Dashboards');
            if (!dashboardNode) {
                return;
            }
            const groupList = Array.isArray(treeResp?.data)
                ? treeResp.data
                : [];
            if (!groupList[0]) {
                return;
            }
            const groupNodes = this.buildGroupNodes(groupList[0], dashboardsById);
            const children = this.userInfoService.isCurrentUserSuperAdmin()
                ? [buildAddNewDashboardNode(), ...groupNodes]
                : groupNodes;
            this.setNodeChildren(dashboardNode, children);
            // Ensure the current route's dashboard is expanded/selected after data loads
            this.highlightCurrentRoute();
            this.refreshFilteredNodes();
        }
        catch {
            // ignore
        }
        finally {
            this.cdr.detectChanges();
        }
    }
    /**
     * Sync the filtered nodes with the current search text (non-debounced).
     */
    refreshFilteredNodes() {
        const search = (this.filterText || '').trim();
        if (search) {
            this.filteredNodes = this.performInPlaceFilter(this.nodes, search, true);
        }
        else {
            this.restoreAllChildren(this.nodes);
            this.filteredNodes = [...this.nodes];
        }
    }
    /**
     * Create a dashboard node and add it to the Dashboards lookup map
     *
     * @param {object} dashboard - Dashboard data
     * @param {string} dashboard.id - Dashboard ID
     * @param {string} dashboard.name - Dashboard name
     * @param {string} [parentKey] - Optional ID of the parent group to ensure uniqueness
     * @returns {TreeNode}
     */
    createDashboardNode(dashboard, parentKey) {
        const node = {
            label: dashboard.name,
            icon: 'fad fa-chart-line',
            type: 'url',
            key: parentKey ? `${dashboard.id}::${parentKey}` : dashboard.id,
            leaf: true,
            data: { state: 'dashboard', params: { id: dashboard.id }, url: `/dashboard/${dashboard.id}` },
        };
        const key = this.getNodeLookupKey(node);
        this.nodeLookupMaps.Dashboards.set(key, node);
        return node;
    }
    /**
     * Build group nodes recursively and incrementally populate the lookup map
     *
     * @param {GroupTreeNode} group - Root group node
     * @param {Map<string, string>} dashboardsById - Dashboard name lookup
     * @returns {TreeNode[]}
     */
    buildGroupNodes(group, dashboardsById) {
        // Returns an array with a single node for root group level; caller uses [0] children
        const build = (groupNode) => {
            // Prepare child nodes and collect dashboard IDs in one pass
            const childNodes = [];
            const childDashIds = new Set();
            if (Array.isArray(groupNode.subGroups)) {
                for (const sg of groupNode.subGroups) {
                    const result = build(sg);
                    if (result.node) {
                        childNodes.push(result.node);
                    }
                    result.dashboardIds.forEach(id => childDashIds.add(id));
                }
            }
            // Prepare dashboard nodes, skipping those already present in children
            const dashboards = Array.isArray(groupNode.dashboards) ? groupNode.dashboards : [];
            const dashboardNodes = [];
            for (const dash of dashboards) {
                const dashId = dash.id;
                if (!childDashIds.has(dashId)) {
                    dashboardNodes.push(this.createDashboardNode({
                        id: dashId,
                        name: dashboardsById.get(dashId) || dash.name || dashId
                    }, groupNode.id));
                }
            }
            // Sort dashboard nodes alphabetically
            dashboardNodes.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
            // Only include this group node if it has dashboards or subgroups
            if (dashboardNodes.length === 0 && childNodes.length === 0) {
                return { node: null, dashboardIds: childDashIds };
            }
            const node = {
                label: groupNode.name,
                icon: 'fad fa-folder',
                key: groupNode.id,
            };
            this.setNodeChildren(node, [...dashboardNodes, ...childNodes]);
            // Add group node to lookup map
            const key = this.getNodeLookupKey(node);
            this.nodeLookupMaps.Dashboards.set(key, node);
            // Collect dashboard IDs at this level
            const idsAtThisLevel = new Set(childDashIds);
            for (const dash of dashboards) {
                idsAtThisLevel.add(dash.id);
            }
            return { node, dashboardIds: idsAtThisLevel };
        };
        // Build from provided root group and return its children for display
        const { node } = build(group);
        const children = Array.isArray(node?.children) ? node.children : [];
        return children;
    }
    highlightCurrentRoute() {
        const path = (window.location && window.location.pathname) || '';
        if (!path) {
            return;
        }
        // Navigation event detected: queue a scroll for when the node becomes visible
        this.shouldScrollToSelected = true;
        const sectionInfo = this.determineSectionFromPath(path);
        if (sectionInfo.isRoot) {
            this.handleRootPath();
        }
        else if (sectionInfo.sectionToExpand) {
            this.handleSectionPath(sectionInfo, path);
        }
        this.scrollSelectedIntoView();
    }
    scrollSelectedIntoView() {
        if (this.selectedNode && this.treeNodes && this.shouldScrollToSelected) {
            setTimeout(() => {
                const matchingRef = this.treeNodes.find(ref => ref.nativeElement.getAttribute('data-key') === this.selectedNode?.key);
                if (matchingRef) {
                    matchingRef.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    this.shouldScrollToSelected = false; // Reset after one-time scroll for this navigation
                }
            }, WINDOW_RESIZE_THROTTLE_MS);
        }
    }
    determineSectionFromPath(path) {
        if (path === '/' || path === '') {
            return { isRoot: true, sectionToExpand: null, shouldSearchDashboards: false };
        }
        let sectionToExpand = null;
        let shouldSearchDashboards = false;
        if (path.startsWith('/dashboard') || path.includes('/dashboard')) {
            sectionToExpand = this.nodes.find(node => node.label === 'Dashboards');
            shouldSearchDashboards = true;
        }
        else if (path.startsWith('/reports') || path.includes('/reports')) {
            sectionToExpand = this.nodes.find(node => node.label === 'Reports');
        }
        else if (path.startsWith('/admin') || path.includes('/admin')) {
            sectionToExpand = this.nodes.find(node => node.label === 'Admin');
        }
        return { isRoot: false, sectionToExpand, shouldSearchDashboards };
    }
    handleRootPath() {
        const sitesNode = this.nodes.find(node => node.label === 'Sites');
        if (sitesNode) {
            this.selectedNode = sitesNode;
        }
    }
    handleSectionPath(sectionInfo, path) {
        const { sectionToExpand, shouldSearchDashboards } = sectionInfo;
        if (!sectionToExpand) {
            // For unknown paths, try to find exact match in all nodes
            const matched = this.expandAndSelectByPath(this.nodes, path);
            if (matched) {
                this.refreshFilteredNodes();
            }
            return;
        }
        // If the path matches our last clicked node, use that specific instance to preserve hierarchical context
        if (this.lastClickedNode?.data?.url === path) {
            this.selectedNode = this.lastClickedNode;
            this.expandAllParents(this.lastClickedNode);
            sectionToExpand.expanded = true;
            this.cdr.detectChanges();
            return;
        }
        // If we have a specific section, search only within that section first
        if (shouldSearchDashboards) {
            const matched = this.expandAndSelectByPath(sectionToExpand.children || [], path);
            if (matched) {
                // Collapse all other sections first
                this.collapseAllParentNodes();
                // Expand the dashboards section when a match is found
                sectionToExpand.expanded = true;
                this.cdr.detectChanges();
                return;
            }
        }
        // For admin section, try to find the best matching node
        const matched = this.expandAndSelectByPath(sectionToExpand.children || [], path);
        if (matched) {
            // Collapse all other sections first
            this.collapseAllParentNodes();
            // Expand the parent section when a match is found
            sectionToExpand.expanded = true;
            this.cdr.detectChanges();
            return;
        }
        // If no exact match, try to find the closest parent match
        const closestMatch = this.findClosestParentMatch(sectionToExpand.children || [], path);
        if (closestMatch) {
            // Collapse all other sections first
            this.collapseAllParentNodes();
            this.selectedNode = closestMatch;
            this.expandAllParents(closestMatch);
            // Also expand the main section
            sectionToExpand.expanded = true;
            this.cdr.detectChanges();
            return;
        }
        // Collapse all other sections first
        this.collapseAllParentNodes();
        // Expand the matching section
        sectionToExpand.expanded = true;
        this.cdr.detectChanges();
    }
    setupLocationChangeListener() {
        const notify = () => {
            this.highlightCurrentRoute();
            this.cdr.detectChanges();
        };
        // Listen to back/forward
        window.addEventListener('popstate', notify);
        this.eventCleanupFns.push(() => window.removeEventListener('popstate', notify));
        // Only patch history methods once globally to avoid duplicates
        if (!window.__historyPatched) {
            window.__historyPatched = true;
            // Dispatch on pushState/replaceState
            const wrap = (type) => {
                const original = history[type];
                return function (...args) {
                    const result = original.apply(this, args);
                    const event = new Event('locationchange');
                    window.dispatchEvent(event);
                    return result;
                };
            };
            history.pushState = wrap('pushState');
            history.replaceState = wrap('replaceState');
        }
        window.addEventListener('locationchange', notify);
        this.eventCleanupFns.push(() => window.removeEventListener('locationchange', notify));
    }
    setupMobileSidebarListener() {
        // Listen for hamburger menu clicks from top-nav
        const mobileToggleHandler = () => {
            this.toggleSidebar();
        };
        window.addEventListener('toggle-mobile-sidebar', mobileToggleHandler);
        this.eventCleanupFns.push(() => window.removeEventListener('toggle-mobile-sidebar', mobileToggleHandler));
        // Listen for desktop hamburger menu clicks from top-nav
        const desktopToggleHandler = () => {
            this.toggleCollapse();
        };
        window.addEventListener('toggle-desktop-sidebar', desktopToggleHandler);
        this.eventCleanupFns.push(() => window.removeEventListener('toggle-desktop-sidebar', desktopToggleHandler));
    }
    updateSidenavWidthVar() {
        const width = this.isMobileCache ? '0px' : this.collapsed ? '0px' : this.sidenavWidth;
        document.documentElement.style.setProperty('--sidenav-width', width);
    }
    // --- Resize logic (desktop) ---
    isResizing = false;
    resizeStartX = 0;
    defaultWidthPx = 265;
    resizeStartWidthPx = this.defaultWidthPx;
    minWidthPx = 220;
    rafScheduled = false;
    nextWidthPx = null;
    onResizeStart(event) {
        if (this.isMobileCache || this.collapsed) {
            return;
        }
        this.isResizing = true;
        this.resizeStartX = event.clientX;
        this.resizeStartWidthPx = this.parsePx(this.sidenavWidth);
        window.addEventListener('mousemove', this.onResizing);
        window.addEventListener('mouseup', this.onResizeEnd);
        // UX: prevent text selection and show resize cursor globally during drag
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        event.preventDefault();
    }
    onResizing = (event) => {
        if (!this.isResizing) {
            return;
        }
        // Ignore if the drag originates from within an input to avoid stealing focus
        const target = event.target;
        if (target && (target.tagName === 'INPUT' || target.closest('input'))) {
            return;
        }
        const delta = event.clientX - this.resizeStartX;
        let next = this.resizeStartWidthPx + delta;
        next = Math.max(this.minWidthPx, next);
        this.nextWidthPx = next;
        if (!this.rafScheduled) {
            this.rafScheduled = true;
            window.requestAnimationFrame(() => {
                this.rafScheduled = false;
                if (this.nextWidthPx !== null) {
                    this.sidenavWidth = `${this.nextWidthPx}px`;
                    this.updateSidenavWidthVar();
                    this.nextWidthPx = null;
                }
            });
        }
    };
    onResizeEnd = () => {
        if (!this.isResizing) {
            return;
        }
        this.isResizing = false;
        window.removeEventListener('mousemove', this.onResizing);
        window.removeEventListener('mouseup', this.onResizeEnd);
        // Persist
        localStorage.setItem('sidenav-width', this.sidenavWidth);
        // Restore body styles
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    };
    parsePx(value) {
        const match = (/^(\d+)px$/).exec(value);
        return match ? Number(match[1]) : this.defaultWidthPx;
    }
    collapseAllParentNodes() {
        this.nodes.forEach((node) => {
            if (this.topLevelKeys.has(String(node.label))) {
                node.expanded = false;
            }
        });
    }
    expandAndSelectByPath(nodes, path) {
        let bestMatch = null;
        let bestMatchLength = 0;
        for (const node of nodes) {
            const url = node?.data?.url;
            if (url) {
                const isMatch = url === '/' ? path === '/' : path.startsWith(url);
                if (isMatch && url.length > bestMatchLength) {
                    bestMatch = node;
                    bestMatchLength = url.length;
                }
            }
            if (Array.isArray(node.children) && node.children.length) {
                const foundInChild = this.expandAndSelectByPath(node.children, path);
                if (foundInChild) {
                    node.expanded = true;
                    return true;
                }
            }
        }
        if (bestMatch) {
            this.selectedNode = bestMatch;
            return true;
        }
        return false;
    }
    findClosestParentMatch(nodes, path) {
        let bestMatch = null;
        let bestMatchLength = 0;
        for (const node of nodes) {
            const url = node?.data?.url;
            if (url && path.startsWith(url) && // This node's URL is a prefix of the current path
                url.length > bestMatchLength) {
                bestMatch = node;
                bestMatchLength = url.length;
            }
        }
        return bestMatch;
    }
    static ctorParameters = () => [
        { type: UserInfoService },
        { type: DashboardInfoService },
        { type: GroupService },
        { type: ChangeDetectorRef }
    ];
    static propDecorators = {
        treeNodes: [{ type: ViewChildren, args: ['treeNode',] }]
    };
};
SideNavComponent = __decorate([
    Component({
        selector: 'rv-side-nav',
        standalone: true,
        imports: [
            CommonModule,
            ButtonModule,
            SidebarModule,
            MenuModule,
            TreeModule,
            BadgeModule,
            RippleModule,
            FormsModule,
            InputTextModule,
        ],
        template: __NG_CLI_RESOURCE__0,
        changeDetection: ChangeDetectionStrategy.OnPush,
    })
], SideNavComponent);
export { SideNavComponent };
